import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Chip,
} from '@mui/material';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import PropTypes from 'prop-types';

const EntityMultiSelect = ({
  value = [],
  onChange,
  options = [],
  placeholder,
  sx = {},
  PopperProps = {},
  ...props
}) => {
  const autocompleteStyles = {
    width: { xs: '100%', sm: 'auto' },
    minWidth: 'fit-content',
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
    lineHeight: '100%',
    letterSpacing: '-0.02em',
    verticalAlign: 'bottom',
    color: '#383838',
    border: '1px solid #DDDFE3',
    background: '#EFF4FF',
    boxShadow: '0px 4px 10px 0px #0000000D',
    borderRadius: '4px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      bgcolor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '& .MuiInputBase-input': {
        py: 0.75,
        px: 1.25,
        fontFamily: 'IBM Plex Sans, sans-serif',
        fontWeight: 400,
        fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
        lineHeight: '100%',
        letterSpacing: '-0.02em',
        color: '#383838',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root': {
          color: '#383838',
          transition: 'transform .2s ease',
        },
      },
      '&.Mui-expanded .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
  };

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value || []}
      onChange={onChange}
      popupIcon={<KeyboardArrowDownRounded />}
      disablePortal={false}
      sx={{ ...autocompleteStyles, ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              height: 'auto',
              minHeight: '40px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#383838',
              opacity: 1,
              fontFamily: 'IBM Plex Sans, sans-serif',
              fontWeight: 400,
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              lineHeight: '100%',
              letterSpacing: '-0.02em',
            },
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const optionLabel = typeof option === 'string' ? option : option?.label || option?.value || '';
          return (
            <Chip
              key={index}
              label={optionLabel}
              size="small"
              {...getTagProps({ index })}
              sx={{
                fontSize: '0.75rem',
                height: '24px',
                bgcolor: '#DDF3FA',
                color: '#2F5D63',
                '& .MuiChip-deleteIcon': {
                  fontSize: '16px',
                  color: '#2F5D63',
                },
              }}
            />
          );
        })
      }
      PaperComponent={({ children, ...other }) => (
        <Box
          component="div"
          {...other}
          sx={{
            borderRadius: '4px',
            border: '1px solid #E6E6E6',
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
            mt: 0.5,
            overflow: 'visible',
            backgroundColor: '#FFFFFF',
            zIndex: 10001,
            position: 'relative',
            display: 'block',
          }}
        >
          {children}
        </Box>
      )}
      ListboxProps={{
        sx: {
          py: 0.5,
          maxHeight: '300px',
          overflow: 'auto',
          '& .MuiAutocomplete-option': {
            fontSize: '0.875rem',
            px: 1.25,
            py: 0.75,
            transition: 'background-color .15s ease',
            '&:hover': { backgroundColor: '#DDF3FA' },
            '&.Mui-focused': { backgroundColor: '#DDF3FA' },
            '&.Mui-selected': {
              backgroundColor: '#DDF3FA',
              color: '#2F5D63',
              fontWeight: 600,
            },
          },
        },
      }}
      componentsProps={{
        popper: {
          style: { zIndex: 10001 },
          placement: 'bottom-start',
          disablePortal: false,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ],
        },
      }}
      PopperProps={{
        style: { zIndex: 10001 },
        placement: 'bottom-start',
        disablePortal: false,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ],
        ...PopperProps,
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.label || option?.value || '';
      }}
      isOptionEqualToValue={(option, value) => {
        const optionValue = typeof option === 'string' ? option : option?.value;
        const valueValue = typeof value === 'string' ? value : value?.value;
        return optionValue === valueValue;
      }}
      {...props}
    />
  );
};

EntityMultiSelect.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
      }),
    ])
  ),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
      }),
    ])
  ),
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  PopperProps: PropTypes.object,
};

export default EntityMultiSelect;
